import React, { useEffect, useState } from 'react'
import Lottie from 'react-lottie-segments'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'

import { withClickTrack } from 'components/Trackers/ClickTracker'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { TestIDs } from 'test/utils/testIds'
import themeValues from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import ESGTour from './ESGTour.json'
import {
  ArrowsContainer,
  CenterContainer,
  CenterContainerText,
  CenterItems,
  Container,
  GreenOutlineLeaf,
  IconContainer,
  Nav,
  NavContainer,
  StyledArrowLeft,
  StyledArrowRight,
  StyledButton,
  StyledCard,
  StyledCloseXIcon,
  StyledDescription1,
  StyledDescription2,
  StyledHeading,
  StyledMarginArrowRight,
} from './styled-components'

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: ESGTour,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

const EducationFlow = (props: WithFlowRouterProps) => {
  const CenterButton = withClickTrack<any, any>(TRACKER_IDS.ESG_TOUR.CENTER_BUTTON)(StyledButton)
  const RightArrowButton = withClickTrack<any, any>(TRACKER_IDS.ESG_TOUR.RIGHT_ARROW)(IconContainer)
  const LeftArrowButton = withClickTrack<any, any>(TRACKER_IDS.ESG_TOUR.LEFT_ARROW)(IconContainer)
  const XButton = withClickTrack<any, any>(TRACKER_IDS.ESG_TOUR.X_BUTTON)(StyledCloseXIcon)

  const IMAGEWIDTH = 1000
  const IMAGEHEIGHT = 1000
  const { isLoadingRemoteStrings, getRemoteString, StyledRemoteString } = useRemoteStrings()
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [screenWidthResize, setScreenWidthResize] = useState<number>(window.innerWidth)
  const displayItems = [
    {
      title: getRemoteString({
        key: 'portfolio.education.esg.title',
      }),
      titleFontSize: '30px',
      description1: (
        <StyledRemoteString
          stringKey={'portfolio.education.esg.description'}
          modifiers={[{ bold: 600 }, { bold: 600 }, { bold: 600 }]}
        />
      ),
      buttonText: "Let's go",
      hasIconArrow: true,
      buttonWidth: '130px',
      testid: TestIDs.ESG_EDUCATION.LETS_GO_BUTTON,
    },
    {
      title: getRemoteString({
        key: 'portfolio.education.enviromental.title',
      }),
      titleFontSize: '22px',
      description1: getRemoteString({
        key: 'portfolio.education.environmental.description1',
      }),
      displayCenterContainer: true,
      centerContainerText: getRemoteString({
        key: 'portfolio.education.enviromental.carbon_footprint_text',
      }),
      transform: 'rotate(-72deg)',
    },
    {
      title: getRemoteString({
        key: 'portfolio.education.social.title',
      }),
      titleFontSize: '22px',
      description1: getRemoteString({
        key: 'portfolio.education.social.description1',
      }),
      description2: getRemoteString({
        key: 'portfolio.education.social.description2',
      }),
      transform: 'rotate(-144deg)',
    },
    {
      title: getRemoteString({
        key: 'portfolio.education.governance.title',
      }),
      titleFontSize: '22px',
      description1: getRemoteString({
        key: 'portfolio.education.governance.description1',
      }),
      description2: getRemoteString({
        key: 'portfolio.education.governance.description2',
      }),
      transform: 'rotate(-216deg)',
    },
    {
      title: getRemoteString({
        key: 'portfolio.education.performance.title',
      }),
      titleFontSize: '22px',
      description1: (
        <StyledRemoteString
          stringKey={'portfolio.education.performance.description1.markdown'}
          modifiers={[{ italic: true }]}
        />
      ),
      buttonText: 'Done',
      buttonWidth: '84px',
      hasIconArrow: false,
      transform: 'rotate(-288deg)',
      testid: TestIDs.ESG_EDUCATION.DONE_BUTTON,
    },
  ]
  const handleNext = () => {
    setCurrentIndex(currentIndex + 1)
  }
  const handlePrev = () => {
    setCurrentIndex(currentIndex - 1)
  }

  const buttonAction = () => {
    if (currentIndex === 0) {
      setCurrentIndex(currentIndex + 1)
    } else if (currentIndex === displayItems.length - 1) {
      props.route({ from: FlowRoute.ESGEducationFlow, action: 'done' })
    }
  }
  switch (currentIndex) {
    case 1: {
      trackEvent('Screen Viewed', {
        eventData: {
          object_name: 'registrationEsgEducationEnvironmental',
          screen: 'registrationEsgEducationEnvironmental',
          screen_name: 'registrationEsgEducationEnvironmental',
          ...getDefaultEventData('Screen Viewed'),
        },
      })
      break
    }
    case 2: {
      trackEvent('Screen Viewed', {
        eventData: {
          object_name: 'registrationEsgEducationSocial',
          screen: 'registrationEsgEducationSocial',
          screen_name: 'registrationEsgEducationSocial',
          ...getDefaultEventData('Screen Viewed'),
        },
      })
      break
    }
    case 3: {
      trackEvent('Screen Viewed', {
        eventData: {
          object_name: 'registrationEsgEducationGovernance',
          screen: 'registrationEsgEducationGovernance',
          screen_name: 'registrationEsgEducationGovernance',
          ...getDefaultEventData('Screen Viewed'),
        },
      })
      break
    }
    case 4: {
      trackEvent('Screen Viewed', {
        eventData: {
          object_name: 'registrationEsgEducationPerformance',
          screen: 'registrationEsgEducationPerformance',
          screen_name: 'registrationEsgEducationPerformance',
          ...getDefaultEventData('Screen Viewed'),
        },
      })
      break
    }
  }
  const headerAction = () => {
    props.route({
      from: FlowRoute.ESGEducationFlow,
      action: 'done',
    })
  }

  const calculatePositionLeft = (screenWidth: number) => {
    if (screenWidth < themeValues.breakpoints.sm) {
      return -((IMAGEWIDTH - screenWidth) / 2)
    } else {
      // 60 and 120 is the padding outside the card
      if (screenWidth >= 811 && screenWidth <= 929) {
        return -((IMAGEWIDTH - (screenWidth - 120)) / 2)
      } else {
        return -((IMAGEWIDTH - (screenWidth - 60)) / 2)
      }
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setScreenWidthResize(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
  }, [])

  return (
    <>
      {isLoadingRemoteStrings ? (
        <>
          <Spinner />
        </>
      ) : (
        <>
          <NavContainer>
            <Nav>
              <XButton onClick={headerAction} icon={AcornsIcon.Icon.CloseXIcon} />
            </Nav>
          </NavContainer>
          <Container>
            <StyledCard>
              <CenterItems
                key={currentIndex}
                initial={{ opacity: 0.85 }}
                animate={{ opacity: 1 }}
                transition={{
                  default: {
                    delay: 0.2,
                    duration: 0.2,
                  },
                }}
              >
                <StyledHeading fontSize={displayItems[currentIndex].titleFontSize}>
                  {displayItems[currentIndex].title}
                </StyledHeading>
                <StyledDescription1>
                  {displayItems[currentIndex]?.description1}
                  {displayItems[currentIndex]?.title === 'Sustainable Portfolios' && (
                    <span>
                      <b> ESG</b> rating.
                    </span>
                  )}
                </StyledDescription1>
                <StyledDescription2>{displayItems[currentIndex]?.description2}</StyledDescription2>
                {displayItems[currentIndex]?.buttonText && (
                  <CenterButton
                    width={displayItems[currentIndex]?.buttonWidth ?? ''}
                    onPress={() => buttonAction()}
                  >
                    {displayItems[currentIndex].buttonText}{' '}
                    {displayItems[currentIndex]?.hasIconArrow && (
                      <StyledMarginArrowRight icon={AcornsIcon.Icon.UtilityArrowRight} />
                    )}
                  </CenterButton>
                )}
                {displayItems[currentIndex]?.displayCenterContainer && (
                  <CenterContainer>
                    <GreenOutlineLeaf icon={AcornsIcon.Icon.GreenOutlineLeaf} width={19} />
                    <CenterContainerText>
                      {displayItems[currentIndex]?.centerContainerText}
                    </CenterContainerText>
                  </CenterContainer>
                )}
              </CenterItems>
              {currentIndex > 0 && currentIndex < displayItems.length - 1 && (
                <ArrowsContainer
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    default: { duration: 1 },
                  }}
                >
                  <LeftArrowButton
                    data-testid={TestIDs.ESG_EDUCATION.LEFT_ARROW}
                    onClick={() => handlePrev()}
                  >
                    <StyledArrowLeft icon={AcornsIcon.Icon.UtilityArrowLeft} />
                  </LeftArrowButton>
                  <RightArrowButton
                    data-testid={TestIDs.ESG_EDUCATION.RIGHT_ARROW}
                    onClick={() => handleNext()}
                  >
                    <StyledArrowRight icon={AcornsIcon.Icon.UtilityArrowRight} />
                  </RightArrowButton>
                </ArrowsContainer>
              )}
              <MediaQuery maxWidth={929}>
                <Lottie
                  options={defaultOptions}
                  style={{
                    position: 'absolute',
                    top: screenWidthResize <= 420 ? 300 : 325,
                    left: calculatePositionLeft(screenWidthResize),
                    pointerEvents: 'none',
                    transition: '1000ms',
                    transitionTimingFunction: 'cubic-bezier(0.23, 1.25, 0.46, 1)',
                    transform: displayItems[currentIndex]?.transform,
                  }}
                  width={IMAGEWIDTH}
                  height={IMAGEHEIGHT}
                />
              </MediaQuery>
              <MediaQuery minWidth={930}>
                <Lottie
                  options={defaultOptions}
                  style={{
                    position: 'absolute',
                    top: 327,
                    right: -180,
                    pointerEvents: 'none',
                    transition: '1000ms',
                    transitionTimingFunction: 'cubic-bezier(0.23, 1.25, 0.46, 1)',
                    transform: displayItems[currentIndex]?.transform,
                  }}
                  width="1160px"
                  height="1158px"
                />
              </MediaQuery>
            </StyledCard>
          </Container>
        </>
      )}
    </>
  )
}

export default withFlowRouter(EducationFlow)
